import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import Modal from './Modal';
import Button from './Button';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: () => void;
}

const LoginContainer = styled.div`
  padding: 2rem;
  max-width: 400px;
  width: 100%;
  text-align: center;
`;

const LoginHeader = styled.div`
  margin-bottom: 2rem;
`;

const LoginTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 0.5rem 0;
`;

const LoginSubtitle = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 1rem 0;
`;

const LoginDescription = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 2rem;
  line-height: 1.5;
`;

const LoginButton = styled(Button)`
  width: 100%;
  font-size: 1rem;
  padding: 0.875rem 1.5rem;
`;

const Auth0Icon = styled.div`
  width: 24px;
  height: 24px;
  margin-right: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.875rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background: ${({ theme }) => theme.colors.error}10;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.error}30;
`;

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();

  const handleAuth0Login = async () => {
    setError('');
    setIsLoading(true);

    try {
      // This will redirect to Auth0 login page
      await login();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ログインに失敗しました');
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <LoginContainer>
        <LoginHeader>
          <LoginTitle>ログイン</LoginTitle>
          <LoginSubtitle>AI Incident Report System</LoginSubtitle>
        </LoginHeader>

        <LoginDescription>
          安全にログインするためにAuth0認証を使用しています。
          ログインボタンをクリックして認証画面に移動してください。
        </LoginDescription>

        <LoginButton
          onClick={handleAuth0Login}
          loading={isLoading}
          disabled={isLoading}
        >
          <Auth0Icon>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.5 21.5v-9h3v9h-3zm6.5-11.5c0 2.761-2.239 5-5 5h-3v-2h3c1.657 0 3-1.343 3-3s-1.343-3-3-3H7V5h5c2.761 0 5 2.239 5 5z"/>
            </svg>
          </Auth0Icon>
          Auth0でログイン
        </LoginButton>

        {error && <ErrorMessage>{error}</ErrorMessage>}
      </LoginContainer>
    </Modal>
  );
};

export default LoginModal;